import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate } from '../Services/translation';
import { query, searchFacetChange } from '../Actions/FacetedSearch.action';
import { useStateWithCallbackLazy } from 'use-state-with-callback';

const FacetedSearchGroup = ({ group }) => {
  const [collapsed, setCollapsed] = useStateWithCallbackLazy(false);
  const showLessItemCount = useRef(5);
  const list = useRef(null);
  const showLess = useRef(null);
  const showMore = useRef(null);
  const listTop = useRef(null);
  const showLessBottom = useRef(null);
  const showMoreBottom = useRef(null);
  const firstRender = useRef(true);

  const toggleShowMore = useCallback(() => {
    setCollapsed(
      (prevState) => !prevState,
      (collapsed) => {
        list.current.style.height = `${
          (collapsed ? showLessBottom.current : showMoreBottom.current) -
          listTop.current
        }px`;
      }
    );
  }, [setCollapsed]);

  useEffect(() => {
    // Make sure it is executed only once.
    // firstRender is used to prevent eslint warning when having empty dependencies array
    if (!firstRender.current) {
      return;
    }
    firstRender.current = false;

    const {
      height: listHeight,
      top: listPosTop,
    } = list.current.getBoundingClientRect();

    listTop.current = listPosTop;
    showLessBottom.current = showLess.current
      ? showLess.current.getBoundingClientRect().bottom
      : null;
    showMoreBottom.current = showMore.current.getBoundingClientRect().bottom;

    const visible = listHeight !== 0;
    const tooMuchItem = group.options.length > showLessItemCount.current;
    visible && tooMuchItem && toggleShowMore();
  }, [group, toggleShowMore]);

  useEffect(() => {
    const length = group.options.filter((o) => o.quantity > 0).length;
    if (list != undefined) {
      if (length < 5) {
        list.current.style.height = `${50 + 32 * length}px`;
      } else {
        list.current.style.height = `${185}px`;
      }
    }
  }, [group]);

  const isExpired = group.id.includes('Expired_');
  const dispatch = useDispatch();
  const onChange = useCallback(
    (group, item) => dispatch(searchFacetChange(group, item)),
    [dispatch]
  );
  const [isActive, setIsActive] = useState(false);
  return (
    <Fragment>
      {!isExpired && (
        <div
          className="faceted-search__group-header"
          id={`group-${group.id}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsActive(true);
              setTimeout(() => {
                list.current?.querySelector('li[data-first-visible="true"]')?.focus();
              }, 0);
            }
          }}
        >
          {group.label}
        </div>
      )}

      <ul
        className="faceted-search__group"
        style={{ margin: 0 }}
        role="group"
        ref={list}
        tabIndex={-1}
        aria-labelledby={`group-${group.id}`}
      >
        {group.options &&
          group.options.map((item, itemIndex, array) => {
            const isHidden = collapsed && itemIndex >= showLessItemCount.current;
            const tabIndex = isActive && !isHidden ? 0 : -1;
            return (
              <li
                key={`${item.label}-${itemIndex}`}
                className="faceted-search__item"
                role="menuitemcheckbox"
                tabIndex={tabIndex}
                aria-hidden={isHidden ? 'true' : undefined}
                data-first-visible={!isHidden && tabIndex === 0 && itemIndex === 0 ? 'true' : undefined}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(group, item);
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setIsActive(false);
                    document.getElementById(`group-${group.id}`)?.focus();
                  }
                }}
                ref={(elem) => {
                  if (itemIndex === showLessItemCount.current - 1) {
                    showLess.current = elem;
                  }
                  if (itemIndex === array.length - 1) {
                    showMore.current = elem;
                  }
                }}
              >
                <FacetedFilterCheckbox item={item} group={group} />
              </li>
            );
          })}
      </ul>

      {group.options.length > showLessItemCount.current && (
        <span
          className="faceted-search__show-more"
          role="button"
          tabIndex={0}
          onClick={toggleShowMore}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') toggleShowMore();
          }}
          aria-expanded={!collapsed}
        >
          {collapsed
            ? translate('filter.showmore')
            : translate('filter.showless')}
        </span>
      )}
    </Fragment>

  );
};

const FacetedFilterCheckbox = ({ item, group }) => {
  const dispatch = useDispatch();
  const onChange = useCallback(
    (group, item) => dispatch(searchFacetChange(group, item)),
    [dispatch]
  );
  const showQuantity =
    group.id.indexOf('Expired_') === -1 &&
    !isNaN(item.quantity) &&
    item.quantity != null;
  const isExpired = group.id.includes('Expired_');
  if (isExpired) {
    return (
      <div className="switch-button-container">
        {!isNaN(item.quantity) && item.quantity != null && item.quantity > 0 && (
          <Fragment>
            <div className="">{item.label}</div>
            <label className="switch" aria-label={item.label} >
              <input
                type="checkbox"
                onChange={(event) => onChange(group, item)}
                aria-checked={
                  group.selectedOptions != null &&
                  group.selectedOptions.includes(item.id)
                }
                checked={
                  group.selectedOptions != null &&
                  group.selectedOptions.includes(item.id)
                }
                role="switch" 
              />
              <div
                className="filter-slider round"
                tabIndex={0}
                role="switch"
                aria-checked={group.selectedOptions?.includes(item.id)}
                aria-label={item.label}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); 
                    onChange(group, item);
                  }
                }}
                onClick={() => onChange(group, item)}
              ></div>
            </label>
          </Fragment>
        )}
      </div>
    );
  } else {
    return (
      <div>
        {!isNaN(item.quantity) && item.quantity != null && item.quantity > 0 && (
          <label className="faceted-filter">
            <input
              className="faceted-filter__input"
              type="checkbox"
              onChange={(event) => onChange(group, item)}
              checked={
                group.selectedOptions != null &&
                group.selectedOptions.includes(item.id)
              }
            />
            <span className="faceted-filter__label">
              {item.label}
              {showQuantity && (
                <span className="faceted-filter__quantity">
                  &nbsp;({item.quantity})
                </span>
              )}
            </span>
          </label>
        )}
      </div>
    );
  }
};

const FacetedSearch = ({ isSlider }) => {
  const dispatch = useDispatch();
  const { facetFilters, navigationTheme } = useSelector(
    (state) => state.facetedSearch
  );

  useEffect(() => {
    // listen to history events (back, forward) of window
    window.onpopstate =
      window.onpopstate ||
      ((event) => {
        dispatch(query(window.location.search.substr(1) || ''));
      });
  }, [dispatch]);

  let filteredFacetFilters = [];
  if (facetFilters && facetFilters.length > 0) {
    if (isSlider === 'true') {
      filteredFacetFilters = facetFilters.filter((group) =>
        group.id.includes('Expired_')
      );
    } else {
      filteredFacetFilters = facetFilters.filter(
        (group) => !group.id.includes('Expired_')
      );
    }
  }
  return (
    navigationTheme !== 'category' &&
    filteredFacetFilters &&
    filteredFacetFilters.length > 0 && (
      <div tabIndex={0} className="faceted-search" role="menu">
        {filteredFacetFilters.map((group, groupIndex) => (
          <FacetedSearchGroup
            key={`${group.label}-${groupIndex}`}
            group={group}
            isSlider={isSlider}
          />
        ))}
      </div>
    )
  );
};

export default FacetedSearch;
